import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Receptor } from '../../models/Catalogos';
import { graphqlSchema } from '../../services/graphql.schema';
import { clearGqlTypename } from '../../utils/formatting';

interface ReceptorSearchOptions {
    /** When set, a name search is fired once on mount */
    initialSearch?: string;
    clearTypename?: boolean;
}

/**
 * Debounced client (receptor) lookup by name or RFC, shared by the CFDI forms.
 */
export function useReceptorSearch({ initialSearch, clearTypename = true }: ReceptorSearchOptions = {}) {
    const [receptorOptions, setReceptorOptions] = useState<Receptor[]>([]);
    const [receptoSubject] = useState(new Subject<string>());
    const [receptoSubjectByRfc] = useState(new Subject<string>());

    const [getByName] = useLazyQuery(graphqlSchema.PROFILE.CATALOGOS.getClienteByName, {
        onCompleted: ({ getClienteByName }: { getClienteByName: Receptor[] }) => {
            setReceptorOptions(clearTypename ? getClienteByName.map(c => clearGqlTypename(c)) : getClienteByName);
        },
        onError: (e) => {
            console.error('Error getting by name: ', e.graphQLErrors[0])
        }
    })
    const [getByRfc] = useLazyQuery(graphqlSchema.PROFILE.CATALOGOS.getClienteByRfc, {
        onCompleted: ({ getClienteByRfc }: { getClienteByRfc: Receptor[] }) => {
            setReceptorOptions(clearTypename ? getClienteByRfc.map(c => clearGqlTypename(c)) : getClienteByRfc);
        },
        onError: (e) => {
            console.error('Error getting by name: ', e.graphQLErrors[0])
        }
    })

    useEffect(() => {
        const s = receptoSubject.pipe(debounceTime(200)).subscribe(name => {
            getByName({ variables: { name } })
        })
        const s2 = receptoSubjectByRfc.pipe(debounceTime(200)).subscribe(rfc => {
            getByRfc({ variables: { rfc } })
        })
        if (initialSearch !== undefined) {
            getByName({ variables: { name: initialSearch } })
        }
        return () => {
            s.unsubscribe();
            s2.unsubscribe();
        }
    }, [receptoSubject, receptoSubjectByRfc, getByName, getByRfc, initialSearch])

    return {
        receptorOptions,
        searchReceptorByName: (name: string) => receptoSubject.next(name),
        searchReceptorByRfc: (rfc: string) => receptoSubjectByRfc.next(rfc),
    }
}
